// Pocket import. Accepts the original Pocket CSV export or a ZIP containing
// multiple CSV parts (pocket_part_*.csv). Parses, collapses duplicate URLs
// in-memory (Pocket exports commonly repeat a URL across unread/archive
// re-saves, http/https, www, trailing-slash), deduplicates again against
// existing rows without losing tags or the oldest save date, inserts pending
// articles, and records per-row status in import_items for resumable
// extraction. A bad batch never aborts the whole run; imports.status is always
// finalized.

import { unzipSync, strFromU8 } from "fflate";
import { supabase } from "./supabase";
import { normalizeUrlClient } from "./db";
import type { Import } from "./types";

export interface PocketRow {
  title: string | null;
  url: string;
  time_added: Date | null;
  tags: string[];
  status: "unread" | "archive";
}

export interface ImportProgress {
  processed: number;
  total: number;
  inserted: number;
  duplicates: number;
  failed: number;
}

export async function parsePocketFile(file: File): Promise<{ rows: PocketRow[]; filename: string }> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".zip")) {
    const buf = new Uint8Array(await file.arrayBuffer());
    const entries = unzipSync(buf);
    const csvNames = Object.keys(entries).filter((k) => k.toLowerCase().endsWith(".csv")).sort();
    const rows: PocketRow[] = [];
    for (const k of csvNames) {
      const text = strFromU8(entries[k]);
      rows.push(...parsePocketCsv(text));
    }
    return { rows, filename: file.name };
  }
  if (name.endsWith(".csv")) {
    return { rows: parsePocketCsv(await file.text()), filename: file.name };
  }
  throw new Error("unsupported_file");
}

// Pocket CSV columns: title,url,time_added,tags,status. Some exports omit the
// header; detect by looking for "url" in the first row.
export function parsePocketCsv(text: string): PocketRow[] {
  const rows = parseCsv(text);
  if (rows.length === 0) return [];
  let headerIdx = 0;
  let cols = rows[0].map((c) => c.trim().toLowerCase());
  if (!cols.includes("url")) {
    cols = ["title", "url", "time_added", "tags", "status"];
    headerIdx = -1;
  }
  const idx = (name: string) => cols.indexOf(name);

  const out: PocketRow[] = [];
  for (let i = headerIdx + 1; i < rows.length; i++) {
    const r = rows[i];
    if (r.length === 0 || (r.length === 1 && !r[0])) continue;
    const url = r[idx("url")] ?? "";
    if (!/^https?:\/\//i.test(url)) continue;
    const titleRaw = r[idx("title")];
    const title = titleRaw ? titleRaw.trim() : null;
    const timeRaw = r[idx("time_added")] ?? "";
    const time_added = parseUnix(timeRaw);
    const tagsRaw = r[idx("tags")] ?? "";
    const tags = tagsRaw ? tagsRaw.split(",").map((s) => s.trim()).filter(Boolean) : [];
    const statusRaw = (r[idx("status")] ?? "unread").trim().toLowerCase();
    const status: PocketRow["status"] = statusRaw === "archive" || statusRaw === "archived" ? "archive" : "unread";
    out.push({ title, url, time_added, tags, status });
  }
  return out;
}

function parseUnix(s: string): Date | null {
  const n = Number(s.trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  return new Date(n * 1000);
}

// RFC-4180-ish CSV parser: handles quoted fields, embedded commas, newlines,
// and doubled quotes.
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += c;
      }
      continue;
    }
    if (c === '"') { inQuotes = true; continue; }
    if (c === ",") { row.push(field); field = ""; continue; }
    if (c === "\r") { continue; }
    if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; continue; }
    field += c;
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows;
}

// ---------- in-memory collapse ----------

interface CollapsedGroup {
  normalized: string;
  representative: PocketRow;       // used for title/url when inserting
  tags: Set<string>;
  time_added: Date | null;         // min across copies
  archive: boolean;                // true if any copy is archived
  sourceRows: PocketRow[];         // every original row, for per-row status
}

// Collapse rows by normalized URL so a single import never inserts two rows
// that collide on articles_inbox_unique_url. Tags union, date is the min,
// archive wins if any copy is archived.
export function collapseRows(rows: PocketRow[]): CollapsedGroup[] {
  const groups = new Map<string, CollapsedGroup>();
  for (const r of rows) {
    const key = normalizeUrlClient(r.url);
    if (!key) continue;
    const g = groups.get(key);
    if (!g) {
      groups.set(key, {
        normalized: key,
        representative: r,
        tags: new Set(r.tags),
        time_added: r.time_added,
        archive: r.status === "archive",
        sourceRows: [r],
      });
    } else {
      for (const t of r.tags) g.tags.add(t);
      if (r.time_added && (!g.time_added || r.time_added < g.time_added)) g.time_added = r.time_added;
      if (r.status === "archive") g.archive = true;
      g.sourceRows.push(r);
    }
  }
  return Array.from(groups.values());
}

// ---------- importer ----------

export async function importPocketRows(
  rows: PocketRow[],
  filename: string,
  onProgress: (p: ImportProgress) => void,
): Promise<Import> {
  const progress: ImportProgress = { processed: 0, total: rows.length, inserted: 0, duplicates: 0, failed: 0 };

  const { data: imp, error: impErr } = await supabase
    .from("imports")
    .insert({ filename, total: rows.length, status: "running" })
    .select("*")
    .single();
  if (impErr) throw impErr;
  const importId = (imp as Import).id;

  try {
    const groups = collapseRows(rows);

    // One batched tag upsert. Dedupe case-insensitively first: tags.name is
    // citext, and two case-variants in one upsert payload would make
    // ON CONFLICT touch the same row twice (Postgres error).
    const tagNameToId = new Map<string, string>();
    {
      const byFold = new Map<string, string>();
      for (const g of groups) {
        for (const raw of g.tags) {
          const clean = raw.trim();
          if (clean && !byFold.has(clean.toLowerCase())) byFold.set(clean.toLowerCase(), clean);
        }
      }
      const names = Array.from(byFold.values());
      if (names.length) {
        const { data } = await supabase
          .from("tags")
          .upsert(names.map((name) => ({ name })), { onConflict: "user_id,name" })
          .select("id, name");
        for (const t of (data ?? []) as Array<{ id: string; name: string }>) {
          tagNameToId.set(t.name.toLowerCase(), t.id);
        }
      }
    }
    const tagIdsFor = (g: CollapsedGroup): string[] =>
      Array.from(g.tags)
        .map((t) => tagNameToId.get(t.trim().toLowerCase()))
        .filter((v): v is string => Boolean(v));

    // Prefetch every existing normalized_url once and dedup in memory.
    // Replaces one round-trip per URL — the difference between minutes and
    // hours on a 10k+ import. Keyset pagination on the PK: offset paging
    // without a stable order can skip rows (PostgREST has no implicit
    // ordering), and a skipped row would resurface as a unique-index
    // violation that fails its whole insert batch. Prefer the live inbox row
    // over an archived one when both exist so merges land on the visible
    // article.
    type ExistingRow = { id: string; normalized_url: string; original_saved_at: string | null; archived_at: string | null };
    const existing = new Map<string, ExistingRow>();
    for (let lastId = ""; ; ) {
      let q = supabase
        .from("articles")
        .select("id, normalized_url, original_saved_at, archived_at")
        .order("id", { ascending: true })
        .limit(1000);
      if (lastId) q = q.gt("id", lastId);
      const { data, error } = await q;
      if (error) throw error;
      const page = (data ?? []) as ExistingRow[];
      for (const r of page) {
        const prev = existing.get(r.normalized_url);
        if (!prev || (prev.archived_at && !r.archived_at)) existing.set(r.normalized_url, r);
      }
      if (page.length < 1000) break;
      lastId = page[page.length - 1].id;
    }

    const BATCH = 200;
    for (let i = 0; i < groups.length; i += BATCH) {
      const batch = groups.slice(i, i + BATCH);

      // Accumulate all writes for the batch; flush each as ONE request.
      const itemRows: ItemRowPayload[] = [];
      const mergeLinks: Array<{ article_id: string; tag_id: string }> = [];
      const toInsert: Array<{ group: CollapsedGroup; tagIds: string[] }> = [];

      for (const group of batch) {
        const tagIds = tagIdsFor(group);
        const ex = existing.get(group.normalized);
        if (ex) {
          // Merge: oldest save date wins, archive wins, tags union.
          const patch: Record<string, unknown> = {};
          if (group.time_added) {
            const newSaved = group.time_added.toISOString();
            if (!ex.original_saved_at || newSaved < ex.original_saved_at) patch.original_saved_at = newSaved;
          }
          if (group.archive && !ex.archived_at) {
            patch.archived_at = group.time_added?.toISOString() ?? new Date().toISOString();
          }
          if (Object.keys(patch).length) {
            await supabase.from("articles").update(patch).eq("id", ex.id);
          }
          for (const tid of tagIds) mergeLinks.push({ article_id: ex.id, tag_id: tid });
          pushItemRows(itemRows, importId, group.sourceRows, "duplicate", ex.id, null);
          progress.duplicates += group.sourceRows.length;
          progress.processed += group.sourceRows.length;
        } else {
          toInsert.push({ group, tagIds });
        }
      }

      // Insert the not-yet-seen articles as one batch. A failure marks those
      // items failed but never aborts the import.
      if (toInsert.length) {
        const articleRows = toInsert.map(({ group }) => ({
          url: group.representative.url,
          normalized_url: group.normalized,
          title: group.representative.title,
          extraction_status: "pending",
          saved_at: new Date().toISOString(),
          original_saved_at: group.time_added?.toISOString() ?? null,
          archived_at: group.archive ? (group.time_added?.toISOString() ?? new Date().toISOString()) : null,
        }));
        const insRes = await supabase.from("articles").insert(articleRows).select("id");
        if (insRes.error || !insRes.data) {
          for (const { group } of toInsert) {
            pushItemRows(itemRows, importId, group.sourceRows, "failed", null, insRes.error?.message ?? "insert_failed");
            progress.failed += group.sourceRows.length;
            progress.processed += group.sourceRows.length;
          }
        } else {
          const insertedArticles = insRes.data as Array<{ id: string }>;
          for (let k = 0; k < toInsert.length; k++) {
            const group = toInsert[k].group;
            const articleId = insertedArticles[k]?.id ?? null;
            if (articleId) {
              for (const tid of toInsert[k].tagIds) mergeLinks.push({ article_id: articleId, tag_id: tid });
              // Keep the in-memory index current so a re-run within this
              // session (or a concurrent save) merges instead of colliding.
              existing.set(group.normalized, {
                id: articleId,
                normalized_url: group.normalized,
                original_saved_at: group.time_added?.toISOString() ?? null,
                archived_at: group.archive ? (group.time_added?.toISOString() ?? new Date().toISOString()) : null,
              });
            }
            const [primary, ...rest] = group.sourceRows;
            pushItemRows(itemRows, importId, [primary], "pending", articleId, null);
            if (rest.length) pushItemRows(itemRows, importId, rest, "duplicate", articleId, null);
            progress.inserted += 1;
            progress.duplicates += rest.length;
            progress.processed += group.sourceRows.length;
          }
        }
      }

      // Flush the batch: one link upsert, one item insert, one checkpoint.
      if (mergeLinks.length) {
        await supabase.from("article_tags").upsert(mergeLinks, { onConflict: "article_id,tag_id" });
      }
      if (itemRows.length) {
        await supabase.from("import_items").insert(itemRows);
      }
      // Checkpoint the imports row so an interrupted tab still shows real
      // progress instead of a stuck 'running' with zero counts.
      await supabase
        .from("imports")
        .update({ inserted: progress.inserted, failed: progress.failed })
        .eq("id", importId);

      onProgress({ ...progress });
    }
  } finally {
    // Always finalize so the import is not stuck at 'running'.
    const { data: finalImp, error } = await supabase
      .from("imports")
      .update({
        status: "completed",
        inserted: progress.inserted,
        failed: progress.failed,
        completed_at: new Date().toISOString(),
      })
      .eq("id", importId)
      .select("*")
      .single();
    if (error) throw error;
    return finalImp as Import;
  }
}

interface ItemRowPayload {
  import_id: string;
  url: string;
  title: string | null;
  tags: string[];
  saved_at: string | null;
  status: "pending" | "duplicate" | "failed";
  article_id: string | null;
  error: string | null;
}

function pushItemRows(
  out: ItemRowPayload[],
  importId: string,
  rows: PocketRow[],
  status: "pending" | "duplicate" | "failed",
  articleId: string | null,
  errorMsg: string | null,
): void {
  for (const r of rows) {
    out.push({
      import_id: importId,
      url: r.url,
      title: r.title,
      tags: r.tags,
      saved_at: r.time_added?.toISOString() ?? null,
      status,
      article_id: articleId,
      error: status === "failed" ? errorMsg : null,
    });
  }
}

export async function listImports(): Promise<Import[]> {
  const { data, error } = await supabase.from("imports").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as Import[];
}

export interface ImportItemSummary {
  status: string;
  count: number;
}

// Live extraction progress for an import. import_items.status is frozen at
// import time (extraction never writes it), so we join through article_id to
// the articles table's CURRENT extraction_status. Filtering import_items to
// status='pending' selects exactly the one primary row per inserted article,
// so nothing double-counts. Server-side count=exact avoids the 1000-row cap.
export async function importExtractionProgress(importId: string): Promise<ImportItemSummary[]> {
  const statuses = ["ready", "pending", "extracting", "failed"] as const;
  const results = await Promise.all(
    statuses.map(async (status) => {
      const { count, error } = await supabase
        .from("import_items")
        .select("article_id, articles!inner(extraction_status)", { count: "exact", head: true })
        .eq("import_id", importId)
        .eq("status", "pending")
        .eq("articles.extraction_status", status);
      if (error) throw error;
      return { status, count: count ?? 0 };
    }),
  );
  return results.filter((r) => r.count > 0);
}
